import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import classnames from 'classnames';
import VideoJS from 'video.js';
// import 'videojs-contrib-hls';
// import "@videojs/http-streaming";
import 'video.js/dist/video-js.min.css';
// import TitleBar from '@/components/VideoPlayer/components/TitleBar';
import TimeDisplay from './components/TimeDisplay';
import ErrorDisplay from './components/ErrorDisplay';
import './index.less';

interface Resource {
  src: string;
  type?: string;
}

export interface PropTypes {
  id?: string;
  posters: string[];
  sources: Resource[];
  options?: any;
  onReady?: (player: any) => void;
  className?: string;
  style?: React.CSSProperties;
}

const DEFAULT_OPTIONS = {
  autoSetup: true,
  controls: true,
  autoplay: false,
  muted: false,
  preload: 'metadata',
  metadata: 'none',
  fluid: true,
  language: 'zh-CN',
  sources: [],
  aspectRatio: '16:9',
  notSupportedMessage: '',
  errorDisplay: true, // 发生错误时，是否展示错误提示
  // 将此设置为true将更改不支持 HTML5 全屏 API 但支持视频元素全屏的设备（即 iPhone）上的全屏行为。播放器将被拉伸以填充浏览器窗口，而不是使视频全屏显示。
  preferFullWindow: true,
  loadingSpinner: true,
  // 播放、暂停按钮 大的那个
  bigPlayButton: true,
  // playbackRates: [0.5, 1, 1.5, 2], // 播放速率
  // 底部操作栏
  controlBar: {
    // playToggle: true, // 播放、暂停按钮
    // // volumePanel: true, // 声音调节按钮
    // volumePanel: {
    //   inline: false, // 纵向展开
    // },
    // progressControl: true, // 播放进度控制条
    // remainingTimeDisplay: false, // 当前播放剩余时间
    // pictureInPictureToggle: false, // 画中画
    // fullscreenToggle: true, // 全屏按钮
    children: [
      { name: 'PlayToggle' },
      { name: 'TimeDisplay' },
      { name: 'ProgressControl' },
      { name: 'VolumePanel', inline: false },
      { name: 'FullscreenToggle' },
    ],
  },
};

const VideoPlayer = (props: PropTypes, ref: React.Ref<any>) => {
  const {
    id = 'screenshots-video-player',
    posters = [],
    sources = [],
    options = {},
    onReady = () => { },
    className,
    style,
  } = props;

  const videoRef = useRef<any>(null);
  const playerRef = useRef<any>(null);
  const [currentIndex] = useState(0);

  // 提供父组件调用的方法
  useImperativeHandle(ref, () => ({
    // 播放
    play() {
      if (playerRef.current) {
        playerRef.current.play();
      }
    },
    // 暂停
    pause() {
      if (playerRef.current) {
        playerRef.current.pause();
      }
    },
    // 获取player实例
    player() {
      return playerRef.current;
    },
  }));

  const registerComponents = () => {
    // VideoJS.registerComponent('TitleBar', TitleBar);
    VideoJS.registerComponent('TimeDisplay', TimeDisplay);
    VideoJS.registerComponent('ErrorDisplay', ErrorDisplay);
  };

  useEffect(() => {
    const videoEl = videoRef.current;
    registerComponents();
    playerRef.current = VideoJS(
      videoEl,
      VideoJS.mergeOptions(DEFAULT_OPTIONS, options),
      () => {
        const player = playerRef.current;
        // player.addChild('TitleBar', { title: '测试标题' });

        onReady && onReady(player);

        player.on('loadstart', function () {
          //客户端开始请求数据
          player.removeChild('ErrorDisplay');
        });

        player.on('loadedmetadata', () => {
          //成功获取资源长度
          player.removeChild('ErrorDisplay');
        });
        player.on('error', () => {
          player.removeChild('ErrorDisplay');
          player.addChild('ErrorDisplay');
          player.removeChild('BigPlayButton');
        });

        player.on('on-retry', () => {
          player.src(player.currentSrc());
          if (playerRef.current) {
            playerRef.current.play();
            player.removeChild('ErrorDisplay');
          }
        });
      },
    );

    return () => {
      const player = playerRef.current;
      if (player) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (sources?.length === 0) return;
    player.src(sources[currentIndex]);
  }, [sources]);

  useEffect(() => {
    const player = playerRef.current;
    let currentPoster;
    if (!player) return;
    currentPoster = posters[currentIndex] || '';
    player.poster(currentPoster);
  }, [posters]);

  return (
    <div style={style} className={classnames('intelligence-video-player-container', className)}>
      <video
        ref={videoRef}
        className="video-js vjs-big-play-centered intelligence-video-player"
        id={id}
      >
        <p className="vjs-no-js">
          To view this video please enable JavaScript, and consider upgrading to
          a web browser that supports HTML5 video
        </p>
      </video>
    </div>
  );
};

export default forwardRef(VideoPlayer);
